import React from 'react';
import { View, StyleSheet, Alert } from 'react-native';
import Animated, { useAnimatedStyle, interpolate, Extrapolation, type SharedValue } from 'react-native-reanimated';
import moment from 'moment';
import { SvgIcon } from '../../components/common/SvgIcon';
import { Clickable, PRESS_PRESETS } from '../../components/common/Clickable';
import { ScrollChromeUnderlay } from '../../components/common/ScrollChromeUnderlay';
import { useTranslation, formatNumber, getLocale } from '../../shared/i18n';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { androidFlatSurfaceStyle, androidScaleFont, androidScaleSize } from '../../shared/theme/sharedStyles';
import { useRtl } from '../../shared/i18n/useRtl';
import { DASHBOARD_HEADER_EXPANDED_HEIGHT, DASHBOARD_HEADER_COLLAPSED_HEIGHT, DASHBOARD_HEADER_SCROLL_THRESHOLD } from '../../shared/theme/studio/dashboardConstants';

const HEADER_GREETING_FONT_EXPANDED = androidScaleFont(26);
const HEADER_GREETING_FONT_COLLAPSED = androidScaleFont(18);
const DASHBOARD_DATE_MARGIN = androidScaleSize(4);
const HEADER_INNER_PAD_BOTTOM = appTheme.spacing.xs;
const HEADER_SECONDARY_FADE_SCROLL = DASHBOARD_HEADER_SCROLL_THRESHOLD * 0.58;

function useStickyHeaderShellStyle(
  scroll_y: SharedValue<number>,
  safe_top_inset: number,
  expanded_height = DASHBOARD_HEADER_EXPANDED_HEIGHT,
) {
  return useAnimatedStyle(() => ({
    height:
      interpolate(
        scroll_y.value,
        [0, DASHBOARD_HEADER_SCROLL_THRESHOLD * 0.75, DASHBOARD_HEADER_SCROLL_THRESHOLD],
        [expanded_height, DASHBOARD_HEADER_COLLAPSED_HEIGHT, DASHBOARD_HEADER_COLLAPSED_HEIGHT],
        Extrapolation.CLAMP,
      ) + safe_top_inset,
  }));
}

function useStickyHeaderInnerStyle(scroll_y: SharedValue<number>) {
  return useAnimatedStyle(() => ({
    paddingBottom: interpolate(
      scroll_y.value,
      [0, DASHBOARD_HEADER_SCROLL_THRESHOLD],
      [HEADER_INNER_PAD_BOTTOM, 8],
      Extrapolation.CLAMP,
    ),
  }));
}

interface DashboardStickyHeaderProps {
  colors: ThemeColors;
  user_name: string;
  scroll_y: SharedValue<number>;
  top_inset: number;
}

/**
 * v3: compact — time-based greeting + first name, date line, language pill. Subscription lives in scroll below.
 */
export function DashboardStickyHeader({ colors, user_name, scroll_y, top_inset }: DashboardStickyHeaderProps) {
  const rtl = useRtl();
  const safe_top_inset = top_inset;
  const { t, setLocale, getLocaleDisplayName, locale } = useTranslation();
  const raw_first = (user_name?.split(/\s+/)[0] || t('common.there')).trim();
  const first_name = raw_first
    ? raw_first.charAt(0).toLocaleUpperCase() + (raw_first.length > 1 ? raw_first.slice(1).toLocaleLowerCase() : '')
    : t('common.there');
  const hour = new Date().getHours();
  const greeting_key =
    hour >= 5 && hour < 12 ? 'goodMorningName' : hour >= 12 && hour < 17 ? 'goodAfternoonName' : 'goodEveningName';
  const greeting_line = t(`dashboard.${greeting_key}`).replace('{{name}}', first_name);

  const show_language_picker = () => {
    Alert.alert(t('settings.language'), t('settings.appLanguage'), [
      { text: getLocaleDisplayName('en'), onPress: () => setLocale('en') },
      { text: getLocaleDisplayName('ar'), onPress: () => setLocale('ar') },
      { text: t('common.cancel'), style: 'cancel' },
    ]);
  };
  const date_label = React.useMemo(() => {
    const loc = locale === 'ar' || getLocale() === 'ar' ? 'ar' : 'en';
    try {
      moment.locale(loc);
    } catch {
      moment.locale('en');
    }
    const m = moment();
    return `${m.format('dddd')}, ${m.format('MMM')} ${formatNumber(m.date())}`;
  }, [locale]);

  const header_style = useStickyHeaderShellStyle(scroll_y, safe_top_inset);
  const header_inner_style = useStickyHeaderInnerStyle(scroll_y);

  const name_style = useAnimatedStyle(() => {
    const scale = interpolate(
      scroll_y.value,
      [0, DASHBOARD_HEADER_SCROLL_THRESHOLD * 0.7],
      [1, HEADER_GREETING_FONT_COLLAPSED / HEADER_GREETING_FONT_EXPANDED],
      Extrapolation.CLAMP,
    );
    return { transform: [{ scale }] };
  });

  const date_collapse_style = useAnimatedStyle(() => {
    const visible = interpolate(
      scroll_y.value,
      [0, HEADER_SECONDARY_FADE_SCROLL],
      [1, 0],
      Extrapolation.CLAMP,
    );
    return {
      opacity: visible,
      transform: [{ translateY: (1 - visible) * -6 }],
    };
  });

  /**
   * Reanimated `Animated.Text` often ignores `textAlign` when the node is stretched to full width.
   * Pin copy to the Yoga *start* edge (right in RTL) via the block, and keep text metrics locale-aware
   * without `width: '100%'` stretch.
   */
  const copy_text_style = rtl.textStyle({ align: 'start', writing: 'locale', stretch: false });

  return (
    <Animated.View style={[styles.headerWrap, header_style, { paddingTop: safe_top_inset }]}>
      <ScrollChromeUnderlay
        scrollY={scroll_y}
        backgroundColor={colors.surface}
        borderColor={colors.borderSubtle}
        threshold={DASHBOARD_HEADER_SCROLL_THRESHOLD}
      />
      <Animated.View style={[styles.headerInner, header_inner_style]}>
        <View style={styles.row}>
          <View style={styles.greetingBlock}>
            <Animated.Text
              numberOfLines={1}
              style={[styles.greeting, { color: colors.text }, copy_text_style, name_style]}>
              {greeting_line}
            </Animated.Text>
            <Animated.Text
              numberOfLines={1}
              style={[styles.date, { color: colors.textLight }, copy_text_style, date_collapse_style]}>
              {date_label}
            </Animated.Text>
          </View>
          <Clickable
            onPress={show_language_picker}
            accessibilityLabel={t('settings.appLanguage')}
            accessibilityRole="button"
            pressScale={PRESS_PRESETS.button.scale}
            haptics>
            <View style={[styles.langPill, { backgroundColor: colors.surface }]}>
              <SvgIcon name="language" size={20} color={colors.primary} />
            </View>
          </Clickable>
        </View>
      </Animated.View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  headerWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    zIndex: 100,
    overflow: 'hidden',
  },
  headerInner: {
    flex: 1,
    paddingHorizontal: appTheme.spacing.lg,
    paddingTop: appTheme.spacing.sm,
    zIndex: 1,
    justifyContent: 'flex-end',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 0,
    gap: appTheme.spacing.md,
  },
  greetingBlock: {
    flex: 1,
    minWidth: 0,
    /** Ambient RTL maps flex-start → physical right (title/date hug the outer start edge). */
    alignItems: 'flex-start',
  },
  greeting: {
    fontSize: HEADER_GREETING_FONT_EXPANDED,
    fontWeight: '800',
    letterSpacing: 0.2,
  },
  date: {
    fontSize: androidScaleFont(13),
    fontWeight: '500',
    marginTop: DASHBOARD_DATE_MARGIN,
  },
  langPill: {
    width: androidScaleSize(44),
    height: androidScaleSize(44),
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    ...appTheme.shadows.xs,
    ...androidFlatSurfaceStyle(),
  },
});
